import { BaseControlledInputProps } from '../basicControlledInputProps.types';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  InputAdornment,
  TextField,
  debounce,
} from '@mui/material';
import { useState, useCallback, useMemo } from 'react';
import { Path } from 'react-hook-form';
import { useFormTracking } from '../hooks';

export type SelectOption = {
  value: string;
  label: string;
};

type SearchBarProps = {
  label: string;
  options?: string[] | SelectOption[];
  onSearch?: (searchTerm: SelectOption | string | null) => void;
  onTypeaheadSearch?: (searchTerm: string) => void;
  initialValue?: string;
  isLoading?: boolean;
  disabled?: boolean;
  debounceMs?: number;
};
export type ControlledSearchBarProps<
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
> = BaseControlledInputProps<FORM_TYPE, FIELD_NAME> & SearchBarProps;

export const ControlledSearchBar = <
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
>({
  label,
  options = [],
  onSearch,
  onTypeaheadSearch,
  initialValue = '',
  isLoading = false,
  disabled = false,
  debounceMs = 500,
  trackingName,
  sensitive = true,
}: ControlledSearchBarProps<FORM_TYPE, FIELD_NAME>) => {
  const trackingProps = useFormTracking(trackingName, sensitive);
  const [searchValue, setSearchValue] = useState<string>(initialValue);
  const [selectedOption, setSelectedOption] = useState<
    SelectOption | string | null
  >(null);

  const debouncedTypeaheadSearch = useMemo(() => {
    if (!onTypeaheadSearch) return () => null;
    return debounce(onTypeaheadSearch, debounceMs);
  }, [onTypeaheadSearch, debounceMs]);

  const handleInputChange = useCallback(
    (event: any) => {
      const value = event?.target?.value || '';
      setSearchValue(value);
      debouncedTypeaheadSearch(value);
    },
    [debouncedTypeaheadSearch]
  );

  const handleOptionChange = useCallback(
    (event: any, option: SelectOption | string | null) => {
      setSelectedOption(option);
      setSearchValue(typeof option === 'string' ? option : option?.label || '');
      onSearch?.(option);
    },
    [onSearch]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && searchValue.trim()) {
        onSearch?.(searchValue);
      }
    },
    [searchValue, onSearch]
  );

  const processedOptions = useMemo(() => {
    return options.map(option =>
      typeof option === 'string' ? option : { ...option }
    );
  }, [options]);

  return (
    <Autocomplete
      {...trackingProps}
      freeSolo
      selectOnFocus
      disabled={disabled}
      options={processedOptions}
      getOptionDisabled={() => isLoading}
      getOptionLabel={option => {
        if (typeof option === 'string') {
          return option;
        }
        return option?.label || '';
      }}
      filterOptions={options => options}
      onChange={handleOptionChange}
      onInputChange={handleInputChange}
      value={selectedOption}
      inputValue={searchValue}
      renderInput={params => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          placeholder={label}
          label=""
          variant="outlined"
          onKeyDown={handleKeyDown}
          sx={{
            backgroundColor: 'white',
            borderRadius: '16px',
          }}
        />
      )}
    />
  );
};
